<template>
  <transaction-details-template
    header-icon="finance_creditcard-income"
    @close="$emit('close')"
  >
    <div
      slot="title"
      v-text="displayTitle"
    ></div>

    <div
      v-if="isProcessingSafeDeal"
      slot="summary"
      v-d-flex
      v-opacity=".65"
    >
      <base-icon
        v-m-r="5"
        :size="16"
        name="basic_lock-outline"
      ></base-icon>
      <span
        v-text="displayAmount"
      ></span>
    </div>

    <span
      v-else
      slot="summary"
      v-text="displayAmount"
    ></span>

    <template slot="content">
      <div v-if="isSetupPayment" :class="$style.setup">
        <h4 v-text="$t('transaction.header.setup.payment')"></h4>
        <help-link
          v-color="'violet'"
          source="https://help.capusta.space/bill/setup_payment"
        />
      </div>

      <template v-if="isShownSafeDeal">
        <transaction-safe-deal
          :deal="transaction.deal"
          :permission="permission"
        />
        <hr :class="$style.delimiter" />
      </template>

      <sender-information
        :sender="senderContent"
        :payment="paymentContent"
        :rating="ratingContent"
      />

      <template v-if="transaction.commission">
        <hr :class="$style.delimiter" />
        <transaction-commission
          :commission="transaction.commission"
          :currency="transaction.currency"
        />
      </template>

      <template v-if="isShownDescription">
        <hr :class="$style.delimiter" />
        <transaction-description
          :description="transaction.description"
          :additional-params="transaction.additional_params"
        />
      </template>

      <template v-if="isShownBillTariff">
        <hr :class="$style.delimiter" />
        <purchase-bill-tariff
          :transaction="transaction"
        />
      </template>

      <!-- Информация о вознаграждении за транзакцию (опционально) -->
      <template v-else-if="transactionTips">
        <hr :class="$style.delimiter" />
        <applied-transaction-tips
          :variant="transactionTips"
        />
      </template>

      <template v-if="isShownDeclaration">
        <hr :class="$style.delimiter" />
        <transaction-declaration
          :transaction="transaction"
          :permission="permission"
        />
      </template>
    </template>

    <div v-if="permission" slot="footer" :class="$style.footer">
      <!-- Кнопка возврата средств -->
      <refund-button
        v-if="isShownRefundButton"
        :public-id="transaction.public_id"
      />

      <!-- Кнопка просмотра информации о возврате -->
      <refunded-info-button
        v-if="isShownRefundedInfoButton"
        :public-id="transaction.public_id"
      />

      <!-- Кнопка отправки квитанции -->
      <receipt-sender-button
        :description="PROJECT_INFO.description"
        :pay-link="PROJECT_INFO.payLink"
        :public-id="transaction.public_id"
      />
    </div>
  </transaction-details-template>
</template>

<script>
import { mapState } from 'vuex';

import { formatAmount } from 'lib/utils/amount';
import { formatDateToCustomDateFormat } from 'lib/utils/date';

import TildaParamsService from 'services/tilda';
import TransactionService from 'services/transaction';
import TransactionDeclarationService from 'services/transaction/declaration';

import TransactionSafeDeal from 'molecules/transaction/details/TransactionSafeDeal';
import SenderInformation from 'molecules/transaction/details/SenderInformation';
import TransactionCommission from 'molecules/transaction/details/TransactionCommission';
import TransactionDescription from 'molecules/transaction/details/TransactionDescription';
import RefundButton from 'molecules/transaction/details/RefundButton';
import RefundedInfoButton from 'molecules/transaction/details/RefundedInfoButton';
import ReceiptSenderButton from 'molecules/transaction/details/ReceiptSenderButton';
import PurchaseBillTariff from 'molecules/transaction/tariff/PurchaseBillTariff';
import AppliedTransactionTips from 'molecules/transaction/details/AppliedTransactionTips';
import TransactionDeclaration from 'organisms/transaction/declaration/TransactionDeclaration';
import TransactionDetailsTemplate from 'templates/transaction/details/TransactionDetailsTemplate';

import HelpLink from 'molecules/HelpLink';

import { DATE_TIME_FORMAT } from 'definitions/dates';

export default {
  name: 'TransferTransactionDetails',
  components: {
    TransactionSafeDeal,
    SenderInformation,
    TransactionCommission,
    TransactionDescription,
    AppliedTransactionTips,
    PurchaseBillTariff,
    RefundButton,
    RefundedInfoButton,
    ReceiptSenderButton,
    TransactionDeclaration,
    TransactionDetailsTemplate,
    HelpLink,
  },
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    permission: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    displayTitle() {
      const formattedDate = formatDateToCustomDateFormat(this.transaction.created_at, DATE_TIME_FORMAT);

      return `${this.$t('common.transfer')} — ${formattedDate}`;
    },
    displayAmount() {
      const { amount, commission, currency } = this.transaction;

      return formatAmount(amount + commission, currency);
    },
    senderContent() {
      return TransactionService.getSenderContent(this.transaction);
    },
    paymentContent() {
      return TransactionService.getPaymentContent(this.transaction);
    },
    ratingContent() {
      return TransactionService.getRatingContent(this.transaction);
    },
    transactionTips() {
      return TransactionService.getTransactionTips(this.transaction);
    },
    isProcessingSafeDeal() {
      return TransactionService.checkProcessingSafeDeal(this.transaction);
    },
    isShownRefundButton() {
      return TransactionService.checkRefundAvailability(this.transaction);
    },
    isShownRefundedInfoButton() {
      return TransactionService.checkActiveStatusRefund(this.transaction);
    },
    isEmptySenderContent() {
      return TransactionService.checkEmptySenderContent(this.transaction);
    },
    isShownDescription() {
      return TransactionService.checkDescriptionOrTildaParams(this.transaction);
    },
    isShownSafeDeal() {
      return TransactionService.checkSafeDeal(this.transaction);
    },
    isShownBillTariff() {
      return TransactionService.checkBillTariff(this.transaction);
    },
    isShownDeclaration() {
      const checkDeclared = TransactionDeclarationService.checkDeclared(this.transaction);
      const checkAllowDeclaring = TransactionDeclarationService.checkAllowDeclaring(this.MERCHANT_INFO, this.transaction);

      if (this.permission) {
        return checkAllowDeclaring || checkDeclared;
      }

      return checkDeclared;
    },
    isSetupPayment() {
      return TildaParamsService.getSubscriptionTokenBinding(this.transaction.additional_params);
    },
    ...mapState(['MERCHANT_INFO', 'PROJECT_INFO']),
  },
};
</script>

<style lang="sass" module>
  .delimiter
    height: 1px
    margin: 20px 0
    border-color: rgba(39, 40, 60, 0.1)

  .footer
    +flexbox
    +justify-content(space-between)
    +size(100%, auto)

  .setup
    display: flex
    justify-content: space-between
    margin-left: 52px
    padding-bottom: 20px

    h4
      +font-type('semi-bold', 14, 20)
      background: linear-gradient(249.76deg, #24A84D -8.32%, #068ACC 108.85%), rgba(255, 255, 255, 0.5)
      -webkit-background-clip: text
      -webkit-text-fill-color: transparent
</style>
